import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import { usePasswordStrength } from './passwordStrength';
import './PasswordInput.css';

const PasswordInput = ({
    id,
    name,
    value,
    onChange,
    placeholder,
    required = false,
    autoComplete = 'new-password',
    userInputs = [],
    showStrength = true,
    minScore,
    minLength,
    className = '',
}) => {
    const { t, i18n } = useTranslation();
    const [visible, setVisible] = React.useState(false);

    const strength = usePasswordStrength(value, {
        language: i18n.language,
        userInputs,
        minScore,
        minLength,
    });

    const feedbackWarning =
        strength.feedback.warning &&
        t(`password.feedback.${strength.feedback.warning}`, {
            defaultValue: strength.feedback.warning,
        });

    const feedbackSuggestions = (strength.feedback.suggestions || [])
        .map((suggestion) =>
            t(`password.feedback.${suggestion}`, {
                defaultValue: suggestion,
            })
        )
        .filter(Boolean);

    return (
        <div className={`PasswordInput ${className}`.trim()}>
            <InputGroup>
                <Input
                    required={required}
                    type={visible ? 'text' : 'password'}
                    autoComplete={autoComplete}
                    name={name}
                    id={id}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    aria-describedby={showStrength && value ? `${id}-strength` : undefined}
                    data-bwignore="true"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-form-type="other"
                />
                <InputGroupAddon addonType="append">
                    <InputGroupText
                        tag="button"
                        type="button"
                        className="PasswordInput-toggle"
                        onClick={() => setVisible((current) => !current)}
                        aria-label={visible ? t('password.hide') : t('password.show')}
                        aria-pressed={visible}
                    >
                        <i className={`fa ${visible ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                    </InputGroupText>
                </InputGroupAddon>
            </InputGroup>

            {showStrength && value && strength.ready && (
                <div className="PasswordInput-strength" id={`${id}-strength`}>
                    <div className="PasswordInput-strengthBar" aria-hidden="true">
                        <div className={`PasswordInput-strengthBar-fill ${strength.barClass}`} />
                    </div>
                    <div className="PasswordInput-strengthLabel">
                        {t(strength.labelKey)}
                    </div>
                    {value.length < strength.minLength && (
                        <div className="PasswordInput-hint text-muted">
                            {t('password.too_short', { count: strength.minLength })}
                        </div>
                    )}
                    {value.length >= strength.minLength && !strength.acceptable && (
                        <div className="PasswordInput-hint text-danger">
                            {t('password.too_weak')}
                        </div>
                    )}
                    {feedbackWarning && (
                        <div className="PasswordInput-hint text-muted">{feedbackWarning}</div>
                    )}
                    {feedbackSuggestions.map((suggestion) => (
                        <div key={suggestion} className="PasswordInput-hint text-muted">
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const PasswordFormGroup = (props) => (
    <FormGroup>
        <PasswordInput {...props} />
    </FormGroup>
);

export default PasswordInput;
